import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './Signup.css';
import defaultProfilePic from '../../assets/defaultProfilePic.png';
import logo from '../../assets/logo.png';
import api from '../../api';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import countryCodes from '../../data/country-codes.json';

const Signup = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [otp, setOtp] = useState(['', '', '', '']); // 4-digit OTP
  const inputRefs = useRef([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    countryCode: '+91',
    password: '',
    confirmPassword: '',
    profilePicture: null,
  });

  const [preview, setPreview] = useState(defaultProfilePic);
  const [showPassword, setShowPassword] = useState(false);
  const [countryList, setCountryList] = useState([]);

  useEffect(() => {
    setCountryList(countryCodes);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };
  // Add the checkbox state
  const [agreeToTerms, setAgreeToTerms] = useState(false);

  // Handle checkbox change
  const handleCheckboxChange = () => {
    setAgreeToTerms(!agreeToTerms);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData({ ...formData, profilePicture: file });
      setPreview(URL.createObjectURL(file));
    }
  };

  const handleSendOtp = async () => {
    try {
      setIsLoading(true);

      // Check if email already exists
      const emailCheckResponse = await axios.post(api.checkEmailExists, {
        email: formData.email,
      });

      if (emailCheckResponse.data.exists) {
        toast.error('Email already exists. Please login or use another email.');
        setIsLoading(false);
        return;
      }

      // Send OTP with signup flag
      await axios.post(api.sendOtp, {
        email: formData.email,
        isSignup: true,
      });

      toast.success('OTP sent to your email!');
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send OTP');
    } finally {
      setIsLoading(false);
    }
  };



  const handleOtpChange = (index, value) => {
    if (!/^\d?$/.test(value)) return; // Allow only numbers
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Move to next box automatically if a digit is entered
    if (value && index < 3) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpBackspace = (index, value) => {
    if (!value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerifyOtp = async () => {
    try {
      const response = await axios.post(api.verifyOtp, {
        email: formData.email,
        otp: otp.join(''),
      });

      if (response.data.verified) {
        setStep(step + 1);
      }
    } catch (error) {
      toast.error('Invalid OTP');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match!');
      return;
    }

    if (formData.phone.length < 10) {
      toast.error('Phone number must be at least 10 digits long.');
      return;
    }

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('countryCode', formData.countryCode);
      formDataToSend.append('password', formData.password);
      if (formData.profilePicture) {
        formDataToSend.append('profilePicture', formData.profilePicture);
      }

      const response = await axios.post(api.signUp, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const { token, refreshToken, userId } = response.data;
      // console.log('Frontend Signup: userId received:', userId);
      localStorage.setItem('authToken', token);
      if (refreshToken) {
        localStorage.setItem('refreshToken', refreshToken);
      }
      localStorage.setItem('tempUserId', userId);

      toast.success(response.data.message);
      navigate('/user-criteria');
    } catch (error) {
      toast.error(error.response?.data?.message || 'An error occurred during signup');
    }
  };

  const handleKeyPress = (e, action) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      action();
    }
  };

  const renderStep = () => {
    switch (step) {
    case 1:
      return (
        <div className="form-step">
          <h3>Enter Your Name</h3>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            onKeyPress={(e) => handleKeyPress(e, () => {
              if (formData.name && formData.name.trim() !== '') {
                setStep(2);
              }
            })}
            placeholder="Full Name"
            required
            className="signup-name"
          />
          <div className="signup-button-container">
            <button
              type="button"
              className="signup-next-button"
              onClick={() => setStep(2)}
              disabled={!formData.name || formData.name.trim() === ''}
            >
                Next
            </button>
          </div>
        </div>
      );

    case 2:
      return (
        <div className="form-step">
          <h3>Enter Your Email</h3>
          <input
            type="email"
            name="email"
            value={formData.email}
            onChange={handleChange}
            onKeyPress={(e) => handleKeyPress(e, () => {
              if (formData.email && formData.email.trim() !== '' && !isLoading) {
                handleSendOtp();
              }
            })}
            placeholder="Email"
            required
            className="signup-email"
          />
          <div className="signup-button-container">
            <button
              type="button"
              className="signup-back-button"
              onClick={() => setStep(1)}
            >
                Back
            </button>
            <button
              type="button"
              className="signup-next-button"
              onClick={handleSendOtp}
              disabled={!formData.email || formData.email.trim() === '' || isLoading}
            >
              {isLoading ? (
                <div className="loading-spinner-reg"></div>
              ) : (
                'Send OTP'
              )}
            </button>
          </div>
        </div>
      );

    case 3:
      return (
        <div className="form-step">
          <h3>Enter OTP</h3>
          <div className="otp-input-container">
            {otp.map((digit, index) => (
              <input
                key={index}
                type="text"
                className="otp-box"
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Backspace' && handleOtpBackspace(index, e.target.value)) {
                    // Backspace handling is already done in handleOtpBackspace
                  } else if (e.key === 'Enter' && !otp.some(d => d === '')) {
                    e.preventDefault();
                    handleVerifyOtp();
                  }
                }}
                maxLength="1"
                ref={(el) => (inputRefs.current[index] = el)}
              />
            ))}
          </div>
          <div className="signup-button-container">
            <button
              type="button"
              className="signup-back-button"
              onClick={() => setStep(2)}
            >
                Back
            </button>
            <button
              type="button"
              className="signup-next-button"
              onClick={handleVerifyOtp}
              disabled={otp.some(digit => digit === '')}
            >
                Verify OTP
            </button>
          </div>
        </div>
      );

    case 4:
      return (
        <div className="form-step">
          <h3>Enter Phone Number</h3>
          <div className="signup-phone-input-container">
            <select
              name="countryCode"
              className="singup-countryCode"
              value={formData.countryCode}
              onChange={handleChange}
            >
              {countryList.map((country, i) => (
                <option key={i} value={country.code}>
                  {country.code}
                </option>
              ))}
            </select>
            <input
              type="text"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onKeyPress={(e) => handleKeyPress(e, () => {
                if (formData.phone && formData.phone.length >= 10) {
                  setStep(5);
                }
              })}
              placeholder="Phone Number"
              required
              className="signup-phone"
            />
          </div>
          <div className="signup-button-container">
            <button
              type="button"
              className="signup-back-button"
              onClick={() => setStep(3)}
            >
                Back
            </button>
            <button
              type="button"
              className="signup-next-button"
              onClick={() => setStep(5)}
              disabled={!formData.phone || formData.phone.length < 10}
            >
                Next
            </button>
          </div>
        </div>
      );

    case 5:
      return (
        <div className="form-step">
          <h3 className="signup-password-heading">Set Password</h3>
          <div className="signup-password-container">
            <input
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Password"
              required
              className="signup-password-input"
            />
            <span
              className="signup-password-toggle"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FaEyeSlash /> : <FaEye />}
            </span>
          </div>
          <div className="signup-password-container">
            <input
              type={showPassword ? 'text' : 'password'}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleChange}
              placeholder="Confirm Password"
              required
              className="signup-confirm-password-input"
            />
          </div>

          <div className="terms-container">
            <input
              type="checkbox"
              id="terms"
              checked={agreeToTerms}
              onChange={handleCheckboxChange}
            />
            <label htmlFor="terms" className="terms-label">
                I agree to the{' '}
              <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer">
                  Terms and Conditions
              </a>
            </label>
          </div>

          <div className="signup-button-container">
            <button
              type="button"
              className="signup-back-button"
              onClick={() => setStep(4)}
            >
                Back
            </button>
            <button
              type="button"
              className="signup-submit-button"
              onClick={handleSubmit}
              disabled={!agreeToTerms || !formData.password || !formData.confirmPassword || formData.password !== formData.confirmPassword}
            >
                Submit
            </button>
          </div>
        </div>
      );

    default:
      return null;
    }
  };

  return (
    <div className="signup-bg">
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <div className="signup-auth-container">
        <div className="signup-welcome-message">
          <img src={logo} alt="Platform Logo" className="signup-logo-image" />
          <h2>Welcome to UlsaApp!</h2>
          <p className="signup-para">
            {/* Join us as an instructor and share your knowledge with a global community
            of learners. Create engaging courses, connect with students, and make an
            impact! */}
            Empower minds, inspire change! Join our UlsaApp as an
            instructor or student. Share your expertise, unlock new
            opportunities, and shape the future.
          </p>
          <p className="journey">Start your journey today!</p>
          <p className="">Already have an account..!👇</p>
          <button
            className="signup-rg-button"
            onClick={() => (window.location.href = '/login')}
          >
            Login
          </button>
        </div>
        <form className="signup-auth-form" onSubmit={(e) => e.preventDefault()}>
          <h2>Sign-up</h2>
          <div className="signup-profile-picture-container1">
            <label htmlFor="profilePicture" className="signup-custom-file-input1">
              <img
                src={preview}
                alt="Profile Preview"
                className="signup-profile-picture"
              />
            </label>
            <input
              id="profilePicture"
              type="file"
              onChange={handleFileChange}
              accept="image/*"
              style={{ display: 'none' }}
              className="signup-profile"
            />
          </div>
          {renderStep()}
        </form>
      </div>
    </div>
  );
};

export default Signup;
